import { motion, AnimatePresence } from "framer-motion";
import { Search, User, LogOut, LayoutDashboard, Network, RouterIcon, UserCog, Users, Bot, Globe, Building, ChevronDown, CheckCircle2, Settings, FileText, Activity, Logs, Workflow, Wrench, Calendar, Briefcase, Tag, ShieldPlus, Video, ChartLine, Database, FileStack, LayoutGrid, X, Sun, Moon } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { handleLogout } from "../auth/logout";
import { useClientLevel, setStoredLevel } from "../hooks/useClientLevel";
import baseapi from "../services/baseapi";
import { hasPermission } from "../utils/permissionsHelper";
import { ChatAssistant } from "../pages/ChatAssistant";
import { useTheme } from "../context/ThemeContext";



export const Dock = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { theme, toggleTheme } = useTheme();

    const [searchTerm, setSearchTerm] = useState("");
    const [profileOpen, setProfileOpen] = useState(false);
    const profileRef = useRef<HTMLDivElement>(null);
    const profileButtonRef = useRef<HTMLButtonElement>(null);
    const [profilePosition, setProfilePosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

    // Mobile dock menu states
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [mobileSearchTerm, setMobileSearchTerm] = useState("");
    const [isMobileClientDropdownOpen, setIsMobileClientDropdownOpen] = useState(false);

    // Chat Assistant state
    const [isChatOpen, setIsChatOpen] = useState(false);

    const level = useClientLevel();
    const [clients, setClients] = useState<any[]>([]);
    const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false);
    const [clientSearchTerm, setClientSearchTerm] = useState("");
    const clientDropdownRef = useRef<HTMLDivElement>(null);

    const userInfo = localStorage.getItem('user');
    const userName = userInfo ? JSON.parse(userInfo).name : "";
    const role = userInfo ? JSON.parse(userInfo).role : "";

    const syncLevelWithClients = useCallback((freshClients: any[]) => {
        if (!level || level.name === "global" || !level.id) return;
        const matched = freshClients.find((c: any) => c.id === level.id);
        if (!matched) return;

        const hasChanged =
            matched.name !== level.name ||
            matched.display_name !== level.display_name || matched.install_first_agent !== level.install_first_agent;

        if (hasChanged) {
            setStoredLevel({
                ...level,
                name: matched.name,
                display_name: matched.display_name,
                install_first_agent: matched.install_first_agent,
            });
        }
    }, [level]);

    const fetchClientData = async () => {
        try {
            const params = {
                query: JSON.stringify({}),
                skip: 0,
                limit: 100,
                fields: ["name", "display_name", "id"],
            };
            const response = await baseapi.get("/clients", { params });
            if (response.data.status) {
                const fresh = response.data.data;
                setClients(fresh);
                syncLevelWithClients(fresh);
            }
        } catch (error: any) {
            console.error("Failed to fetch clients:", error.message);
        }
    };

    useEffect(() => {
        fetchClientData();
    }, []);

    useEffect(() => {
        if (clients.length > 0) {
            syncLevelWithClients(clients);
        }
    }, [clients, syncLevelWithClients]);

    useEffect(() => {
        const handleClientsUpdate = () => fetchClientData();
        window.addEventListener('clients-updated', handleClientsUpdate);
        return () => window.removeEventListener('clients-updated', handleClientsUpdate);
    }, [fetchClientData]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (clientDropdownRef.current && !clientDropdownRef.current.contains(event.target as Node)) {
                setIsClientDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location.pathname]);

    const handleClientSelect = async (newLevel: any) => {
        // if (newLevel.name !== "global" && newLevel.id) {
        //     try {
        //         const response = await baseapi.get("/installation_secret/get-secret-key", {
        //             params: { client_id: newLevel.id },
        //         });
        //         if (response.data.secret_key) newLevel.secret_key = response.data.secret_key;
        //     } catch (error: any) {
        //         console.error(error.message);
        //     }
        // }

        if (level.name === "global" && location.pathname === "/clients" && newLevel.name !== "global") {
            navigate("/dashboard");
        }

        if (location.pathname.startsWith("/custom-dashboard")) {
            if (newLevel.name === "global") {
                navigate("/dashboard");
            } else {
                navigate("/custom-dashboard-v2");
            }
        }

        if (location.pathname.startsWith("/customreports")) {
            if (newLevel.name === "global") {
                navigate("/dashboard");
            } else if (location.pathname.includes("/builder")) {
                navigate("/customreports");
            }
        }

        setStoredLevel(newLevel);
        setIsClientDropdownOpen(false);
        setIsMobileClientDropdownOpen(false);
        setClientSearchTerm("");
    };

    const filteredClients = clients.filter((client: any) =>
        client.name?.toLowerCase().includes(clientSearchTerm.toLowerCase())
    );

    // const capitalize = (text?: string) =>
    //     text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : "";

    const BASE_DOCK_ITEMS = [
        { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, path: "/dashboard", color: "bg-white dark:bg-purple-500/30", iconColor: "text-purple-500 dark:text-white" },
        // { id: "custom-dashboard", label: "Custom Dashboard", icon: ChartLine, path: "/custom-dashboard", clientOnly: true, color: "bg-white dark:bg-cyan-500/30", iconColor: "text-cyan-500 dark:text-white" },
        { id: "custom-dashboard-v2", label: "Custom Dashboard V2", icon: ChartLine, path: "/custom-dashboard-v2", clientOnly: true, permissionKey: "cdash", color: "bg-white dark:bg-cyan-500/30", iconColor: "text-cyan-500 dark:text-white" },
        { id: "external-scan", label: "External Scan", icon: Globe, path: "/external-scan", permissionKey: "external_scan", clientOnly: true, color: "bg-white dark:bg-amber-500/30", iconColor: "text-amber-500 dark:text-white" },
        { id: "assets", label: "Assets", icon: RouterIcon, path: "/asset", permissionKey: "assets", color: "bg-white dark:bg-sky-600/30", iconColor: "text-sky-600 dark:text-white" },
        { id: "agents", label: "Agents", icon: Bot, path: "/agents", permissionKey: "agents", color: "bg-white dark:bg-gray-600/30", iconColor: "text-gray-600 dark:text-white" },
        { id: "kubernetes", label: "Kubernetes", icon: "/kubernetes.svg", path: "/kubernetes", permissionKey: "k8s_clusters", color: "bg-white dark:bg-teal-600/30", iconColor: "text-teal-600 dark:text-white" },
        { id: "clients", label: "Clients", icon: Users, path: "/clients", permissionKey: "clients", globalOnly: true, color: "bg-white dark:bg-cyan-700/50", iconColor: "text-cyan-600 dark:text-white" },
        { id: "topology", label: "Topology", icon: Network, path: "/topology", permissionKey: "topology", clientOnly: true, color: "bg-white dark:bg-indigo-600/30", iconColor: "text-indigo-600 dark:text-white" },
        // { id: "db-topology", label: "DB Topology", icon: Database, path: "/db-topology", permissionKey: "topology", clientOnly: true, color: "bg-white dark:bg-orange-600/30", iconColor: "text-orange-600 dark:text-white" },
        { id: "db-monitor", label: "DB Monitor", icon: Database, path: "/db-monitor", permissionKey: "topology", clientOnly: true, color: "bg-white dark:bg-emerald-600/30", iconColor: "text-emerald-600 dark:text-white" },
        { id: "cameras", label: "Monitoring System", icon: Video, path: "/cameras", clientOnly: true, permissionKey: "camera", color: "bg-white dark:bg-pink-600/30", iconColor: "text-pink-600 dark:text-white" },
        { id: "reports", label: "Reports", icon: FileText, path: "/reports", permissionKey: "reports", color: "bg-white dark:bg-blue-600/30", iconColor: "text-blue-600 dark:text-white" },
        { id: "custom-reports", label: "Custom Reports", icon: FileStack, path: "/customreports", permissionKey: "custom_reports", clientOnly: true, color: "bg-white dark:bg-fuchsia-600/30", iconColor: "text-fuchsia-600 dark:text-white" },
        { id: "automation", label: "Automation", icon: UserCog, path: "/automation", permissionKey: "rule_engine_config", color: "bg-white dark:bg-orange-600/30", iconColor: "text-orange-600 dark:text-white" },
        { id: "events", label: "Events", icon: Activity, path: "/events", permissionKey: "events", color: "bg-white dark:bg-red-600/30", iconColor: "text-red-600 dark:text-white" },
        { id: "audit-logs", label: "Audit Logs", icon: Logs, path: "/audit-logs", permissionKey: "audit_logs", color: "bg-white dark:bg-amber-600/30", iconColor: "text-amber-600 dark:text-white" },
        { id: "patching", label: "Patching", icon: ShieldPlus, path: "/patching", permissionKey: "agent_patch_jobs", clientOnly: true, color: "bg-white dark:bg-red-500/30", iconColor: "text-red-500 dark:text-white" },
        // { id: "digital-twin", label: "Digital Twin", icon: Globe, path: "/digital-twin", globalOnly: true, color: "bg-white dark:bg-blue-600/30", iconColor: "text-blue-600 dark:text-white" },
        { id: "jobs", label: "Jobs", icon: Briefcase, path: "/jobs", permissionKey: "jobs", color: "bg-white dark:bg-blue-700/30", iconColor: "text-blue-600 dark:text-white", hiddenFromDock: false },
        { id: "settings", label: "Settings", icon: Settings, path: "/settings", color: "bg-white dark:bg-purple-600/30", iconColor: "text-purple-600 dark:text-white" },
        { id: "integrations", label: "Integrations", icon: Workflow, path: "/settings?tab=api_integration", permissionKey: "integrations", color: "bg-white dark:bg-teal-600/30", iconColor: "text-teal-600 dark:text-white", hiddenFromDock: true },
        { id: "configuration", label: "Configuration", icon: Wrench, path: "/settings?tab=configuration", permissionKey: "ticketing_event_configs", color: "bg-white dark:bg-slate-600/30", iconColor: "text-slate-600 dark:text-white", hiddenFromDock: true },
        { id: "scheduler", label: "Scheduler", icon: Calendar, path: "/settings?tab=scheduler", permissionKey: "scheduler", color: "bg-white dark:bg-cyan-600/30", iconColor: "text-cyan-600 dark:text-white", hiddenFromDock: true },
        { id: "tags", label: "Tags", icon: Tag, path: "/settings?tab=tags", permissionKey: "tags", color: "bg-white dark:bg-yellow-600/30", iconColor: "text-yellow-600 dark:text-white", hiddenFromDock: true },
        { id: "users", label: "Users", icon: User, path: "/settings?tab=users", permissionKey: "users", color: "bg-white dark:bg-orange-600/30", iconColor: "text-orange-600 dark:text-white", hiddenFromDock: true },
        { id: "roles", label: "Roles", icon: UserCog, path: "/settings?tab=roles", permissionKey: "roles", color: "bg-white dark:bg-green-600/30", iconColor: "text-green-600 dark:text-white", hiddenFromDock: true },
        { id: "services_monitoring", label: "Services Monitoring", icon: Activity, path: "/settings?tab=services_monitoring", permissionKey: "service_monitor", clientOnly: true, color: "bg-white dark:bg-indigo-600/30", iconColor: "text-indigo-600 dark:text-white", hiddenFromDock: true },
        { id: "db_settings", label: "DB Monitoring", icon: Database, path: "/settings?tab=db_settings", permissionKey: "db_monitor", clientOnly: true, color: "bg-white dark:bg-emerald-600/30", iconColor: "text-emerald-600 dark:text-white", hiddenFromDock: true },
    ];

    // Settings tab permission keys — mirrors SettingsMain BASE_TABS permissionKeys
    const SETTINGS_COMMON_KEYS = ['settings', 'integrations', 'events', 'ticketing_event_configs', 'scheduler', 'tags', 'users', 'roles'];
    const SETTINGS_CLIENT_ONLY_KEYS = ['service_monitor', 'db_monitor'];

    const hasAnySettingsPermission = useMemo(() => {
        const isGlobal = level?.name === 'global';
        const keysToCheck = isGlobal ? SETTINGS_COMMON_KEYS : [...SETTINGS_COMMON_KEYS, ...SETTINGS_CLIENT_ONLY_KEYS];
        return keysToCheck.some(key => hasPermission(key, 'read'));
    }, [level]);

    const DOCK_ITEMS = useMemo(() => {
        return BASE_DOCK_ITEMS.filter(item => {
            if (item.globalOnly && level?.name !== "global") return false;
            if (item.clientOnly && level?.name === "global") return false;
            // Settings entry: only show if user can access at least one settings tab
            if (item.id === "settings") return hasAnySettingsPermission;
            if (item.permissionKey) return hasPermission(item.permissionKey, 'read');
            return true;
        });
    }, [level, hasAnySettingsPermission]);

    const isActive = (path: string) => {
        const basePath = path.split('?')[0];
        return location.pathname.startsWith(basePath);
    };

    const filteredItems = searchTerm.trim() === ""
        ? DOCK_ITEMS.filter(item => !item.hiddenFromDock)
        : DOCK_ITEMS.filter(item =>
            item.label.toLowerCase().includes(searchTerm.toLowerCase())
        );

    const mobileFilteredItems = mobileSearchTerm.trim() === ""
        ? DOCK_ITEMS.filter(item => !item.hiddenFromDock)
        : DOCK_ITEMS.filter(item =>
            item.label.toLowerCase().includes(mobileSearchTerm.toLowerCase())
        );

    useEffect(() => {
        if (!profileOpen) return;
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                profileRef.current && !profileRef.current.contains(target) &&
                profileButtonRef.current && !profileButtonRef.current.contains(target)
            ) {
                setProfileOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [profileOpen]);

    const computeProfilePosition = () => {
        if (!profileButtonRef.current) return;
        const rect = profileButtonRef.current.getBoundingClientRect();
        const panelWidth = 192;
        const panelMargin = 8;
        const estimatedHeight = 150;

        let left = rect.left;
        let top = rect.top - estimatedHeight - panelMargin;

        if (top < panelMargin) top = panelMargin;

        const rightOverflow = left + panelWidth - window.innerWidth;
        if (rightOverflow > 0) left = Math.max(panelMargin, left - rightOverflow - panelMargin);

        setProfilePosition({ top: Math.round(top), left: Math.round(left) });
    };

    const toggleProfile = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!profileOpen) computeProfilePosition();
        setProfileOpen(prev => !prev);
    };

    useEffect(() => {
        if (!profileOpen) return;
        const update = () => computeProfilePosition();
        window.addEventListener("resize", update);
        window.addEventListener("scroll", update, true);
        return () => {
            window.removeEventListener("resize", update);
            window.removeEventListener("scroll", update, true);
        };
    }, [profileOpen]);

    const handleLogoutClick = () => {
        handleLogout();
        setProfileOpen(false);
        setIsMobileMenuOpen(false);
    };

    // Calculate dynamic styles based on client name length to prevent mascot cutoff
    const clientName = level?.name === "global" ? "Global" : (level?.name || "");
    const clientNameLength = clientName.length;
    const isLongName = clientNameLength > 12;
    const isVeryLongName = clientNameLength > 20;

    const buttonPadding = isVeryLongName
        ? "px-2 py-1 min-h-[28px]"
        : isLongName
            ? "px-2.5 py-1.5 min-h-[32px]"
            : "px-3 py-2 min-h-[36px]";
    const textClass = isVeryLongName
        ? "text-[10px]"
        : isLongName
            ? "text-[11px]"
            : "text-xs";
    const textMaxWidth = isVeryLongName
        ? "max-w-[100px]"
        : isLongName
            ? "max-w-[130px]"
            : "max-w-[180px]";

    return (
        <>
            <ChatAssistant isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />

            {/* ── Main Container ──────────────────────────────────────────────── */}
            <div className="hidden lg:block fixed bottom-4 left-0 right-0 mx-auto z-40 px-2 pointer-events-none">
                <div className="relative flex items-end justify-center max-w-fit mx-auto pointer-events-auto">

                    {/* The Glass Dock */}
                    <div className="backdrop-blur-md bg-white/80 dark:bg-gray-900/70 border border-gray-200/80 dark:border-gray-700 rounded-2xl px-3 py-2 flex items-center gap-3 shadow-xl shadow-gray-400/20 dark:shadow-black/40 transition-colors duration-200">

                        {/* Brand Logo */}
                        <div className="flex items-center flex-shrink-0">
                            <button
                                onClick={() => navigate("/dashboard")}
                                className="w-9 h-9 flex items-center justify-center rounded-xl hover:scale-110 transition-transform duration-200 cursor-pointer"
                                title="Dashboard"
                            >
                                <img
                                    src="/owllogopng.png"
                                    alt="Ovul Logo"
                                    className="w-10 h-10 object-contain"
                                />
                            </button>
                        </div>

                        <div className="w-px h-7 bg-gray-200 dark:bg-gray-700 flex-shrink-0" />

                        {/* Nav items */}
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                            {filteredItems.length > 0 ? (
                                filteredItems.map(item => (
                                    <DockIcon
                                        key={item.id}
                                        item={item}
                                        active={isActive(item.path)}
                                        onClick={() => { navigate(item.path); setSearchTerm(""); }}
                                    />
                                ))
                            ) : (
                                <div className="text-sm text-gray-500 dark:text-gray-400 px-2">No results.</div>
                            )}
                        </div>

                        <div className="w-px h-7 bg-gray-200 dark:bg-gray-700 flex-shrink-0" />

                        {/* Search */}
                        <div className="relative flex-shrink-0">
                            <Search className="w-3 h-3 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                placeholder="Search apps..."
                                className="bg-gray-100 dark:bg-gray-800/70 border border-gray-200 dark:border-gray-700 rounded-lg pl-6 pr-3 py-1.5 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-orange-500 w-40 transition-colors"
                            />
                        </div>

                        <div className="w-px h-7 bg-gray-200 dark:bg-gray-700 flex-shrink-0" />

                        {/* Profile button */}
                        <div className="relative group flex-shrink-0">
                            <button
                                ref={profileButtonRef}
                                onClick={toggleProfile}
                                className={`${profileOpen ? "bg-orange-600" : "bg-orange-500"} hover:bg-orange-600 hover:scale-110 text-white p-2.5 rounded-xl transition-all duration-200 hover:shadow-lg flex-shrink-0`}
                            >
                                <User className="w-4 h-4" />
                            </button>
                            {!profileOpen && (
                                <div className="absolute -top-12 left-1/2 -translate-x-1/2 
                px-2 py-1 rounded bg-gray-900/95 dark:bg-gray-800/95 border border-gray-700 
                text-white text-xs opacity-0 group-hover:opacity-100 
                transition-opacity duration-200 pointer-events-none 
                whitespace-nowrap z-50">

                                    Profile

                                    {/* Arrow */}
                                    <div className="absolute left-1/2 -bottom-1.5 -translate-x-1/2 
                  w-2.5 h-2.5 bg-gray-900 dark:bg-gray-800 border-r border-b border-gray-700 
                  rotate-45"></div>
                                </div>
                            )}
                        </div>

                        {/* Theme Toggle button */}
                        <div className="relative group flex-shrink-0">
                            <button
                                onClick={toggleTheme}
                                className="bg-gray-100 dark:bg-gray-800/70 hover:bg-gray-200 dark:hover:bg-gray-700/80 text-amber-500 dark:text-amber-400 hover:scale-110 p-2.5 rounded-xl transition-all duration-200 hover:shadow-lg flex-shrink-0 border border-gray-200 dark:border-gray-700/50"
                                aria-label="Toggle Theme"
                            >
                                {theme === "dark" ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-indigo-500 dark:text-indigo-300" />}
                            </button>
                            <div className="absolute -top-12 left-1/2 -translate-x-1/2 
                px-2 py-1 rounded bg-gray-900/95 dark:bg-gray-800/95 border border-gray-700 
                text-white text-xs opacity-0 group-hover:opacity-100 
                transition-opacity duration-200 pointer-events-none 
                whitespace-nowrap z-50">
                                {theme === "dark" ? "Light Mode" : "Dark Mode"}
                                <div className="absolute left-1/2 -bottom-1.5 -translate-x-1/2 
                  w-2.5 h-2.5 bg-gray-900 dark:bg-gray-800 border-r border-b border-gray-700 
                  rotate-45"></div>
                            </div>
                        </div>

                        {/* Client selector */}
                        {level?.name && (
                            <>
                                <div className="w-px h-6 bg-gray-200 dark:bg-gray-600 flex-shrink-0" />
                                <div className="relative flex-shrink-0" ref={clientDropdownRef}>
                                    <button
                                        onClick={() => setIsClientDropdownOpen(prev => !prev)}
                                        className={`bg-orange-500/10 dark:bg-gradient-to-r dark:from-orange-500/20 dark:to-amber-600/20 border border-orange-400/30 dark:border-orange-500/30 rounded-lg flex items-center gap-1.5 hover:bg-orange-500/15 dark:hover:from-orange-500/30 dark:hover:to-amber-600/30 transition-all duration-200 ${buttonPadding}`}
                                    >
                                        <div className="w-1.5 h-1.5 bg-orange-500 dark:bg-orange-400 rounded-full animate-pulse flex-shrink-0" />
                                        <span className={`text-orange-700 dark:text-orange-300 font-semibold truncate ${textClass} ${textMaxWidth}`}>
                                            {clientName}
                                        </span>
                                        <ChevronDown className={`w-3 h-3 text-orange-500 dark:text-orange-400/70 transition-transform duration-200 flex-shrink-0 ${isClientDropdownOpen ? "rotate-180" : ""}`} />
                                    </button>

                                    <AnimatePresence>
                                        {isClientDropdownOpen && (
                                            <motion.div
                                                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                                animate={{ opacity: 1, y: -8, scale: 1 }}
                                                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                                className="absolute bottom-full right-0 mb-2 w-64 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-[9999] overflow-hidden"
                                            >
                                                {/* Search */}
                                                <div className="p-2 bg-gray-50 dark:bg-gray-950/50 border-b border-gray-100 dark:border-gray-700">
                                                    <div className="relative">
                                                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                                                        <input
                                                            type="text"
                                                            placeholder="Filter clients..."
                                                            value={clientSearchTerm}
                                                            onChange={e => setClientSearchTerm(e.target.value)}
                                                            className="w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-gray-900 dark:text-gray-200 focus:outline-none focus:border-orange-500/50"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="max-h-60 overflow-y-auto custom-scroll py-1">
                                                    {/* Global option */}
                                                    <button
                                                        onClick={() => handleClientSelect({ name: "global" })}
                                                        className="w-full px-3 py-2 flex items-center gap-2.5 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                                    >
                                                        <Globe className="w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400" />
                                                        <div className="text-left flex-1 min-w-0">
                                                            <p className="text-[11px] font-bold text-gray-800 dark:text-gray-200">Global</p>
                                                        </div>
                                                        {level.name === "global" && (
                                                            <CheckCircle2 className="w-3 h-3 text-orange-500" />
                                                        )}
                                                    </button>

                                                    {/* Client list */}
                                                    {filteredClients.map(client => (
                                                        <button
                                                            key={client.id}
                                                            onClick={() => handleClientSelect({
                                                                id: client.id,
                                                                name: client.name,
                                                                display_name: client.display_name,
                                                                client_id: client.client_id,
                                                                install_first_agent: client.install_first_agent,
                                                            })}
                                                            className="w-full px-3 py-2 flex items-center gap-2.5 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                                        >
                                                            <Building className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
                                                            <div className="text-left flex-1 min-w-0">
                                                                <p className="text-[11px] font-bold text-gray-800 dark:text-gray-200 truncate">
                                                                    {client.name}
                                                                </p>
                                                                <p className="text-[9px] text-gray-500 dark:text-gray-400 truncate">
                                                                    {client.display_name || "Enterprise Client"}
                                                                </p>
                                                            </div>
                                                            {level.id === client.id && (
                                                                <CheckCircle2 className="w-3 h-3 text-orange-500" />
                                                            )}
                                                        </button>
                                                    ))}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </>
                        )}
                    </div>

                    {/* ── Owl Mascot & Chat Bubble (Positioned absolutely so it doesn't shift the dock) ── */}
                    <div className="absolute left-full bottom-0 mb-1 flex-shrink-0 pointer-events-auto">
                        <div className="flex flex-row items-end pb-1 whitespace-nowrap">
                            <button onClick={() => setIsChatOpen(!isChatOpen)}>
                                <img
                                    src="/owl.gif"
                                    alt="Owl mascot"
                                    className="w-24 h-16 object-contain hover:scale-110 transition-transform duration-200"
                                />
                            </button>
                            {!isChatOpen ? (
                                <div className="w-60 h-16">
                                    <img src="/bubble.svg" alt="Chat Bubble" />
                                </div>
                            ) : (
                                <div className="w-40 h-16">
                                    <img src="/bubble2.svg" alt="Chat Bubble" />
                                </div>
                            )}
                        </div>
                    </div>

                </div>
            </div>

            {/* ── Mobile Floating Action Button (Bottom Right) ─────────────── */}
            <div className="fixed bottom-5 right-5 z-50 lg:hidden pointer-events-auto">
                <motion.button
                    whileHover={{ scale: 1.06 }}
                    whileTap={{ scale: 0.94 }}
                    onClick={() => setIsMobileMenuOpen(prev => !prev)}
                    className="w-13 h-13 p-3.5 bg-orange-600 text-white rounded-2xl border border-orange-400/40 flex items-center justify-center backdrop-blur-md transition-all duration-200"
                    aria-label="Toggle Apps Menu"
                >
                    <AnimatePresence mode="wait">
                        {isMobileMenuOpen ? (
                            <motion.div
                                key="close"
                                initial={{ rotate: -90, opacity: 0 }}
                                animate={{ rotate: 0, opacity: 1 }}
                                exit={{ rotate: 90, opacity: 0 }}
                                transition={{ duration: 0.15 }}
                            >
                                <X className="w-6 h-6 text-white" />
                            </motion.div>
                        ) : (
                            <motion.div
                                key="grid"
                                initial={{ rotate: 90, opacity: 0 }}
                                animate={{ rotate: 0, opacity: 1 }}
                                exit={{ rotate: -90, opacity: 0 }}
                                transition={{ duration: 0.15 }}
                            >
                                <LayoutGrid className="w-6 h-6 text-white" />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.button>
            </div>

            {/* ── Mobile Apps Popup Menu / Drawer ───────────────────────────── */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <>
                        {/* Backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
                        />

                        {/* Floating Popup Sheet */}
                        <motion.div
                            initial={{ opacity: 0, y: 30, scale: 0.94 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 30, scale: 0.94 }}
                            transition={{ type: "spring", stiffness: 350, damping: 28 }}
                            className="fixed bottom-20 right-4 left-4 sm:left-auto sm:right-5 sm:w-96 max-h-[82vh] bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border border-gray-200 dark:border-gray-700/80 rounded-3xl shadow-2xl shadow-gray-400/30 dark:shadow-black/80 z-50 flex flex-col overflow-hidden lg:hidden transition-colors"
                        >
                            {/* Header: User profile info + quick actions */}
                            <div className="p-3.5 border-b border-gray-100 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-950/50">
                                <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2.5 min-w-0">
                                        <div className="w-9 h-9 bg-gradient-to-tr from-orange-600 to-amber-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md shadow-orange-500/20">
                                            <User className="w-4.5 h-4.5 text-white" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-gray-900 dark:text-white text-xs font-semibold truncate">{userName || "User"}</p>
                                            <p className="text-gray-500 dark:text-gray-400 text-[10px] truncate">{role || "Member"}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        <button
                                            onClick={toggleTheme}
                                            className="p-2 rounded-xl text-amber-500 dark:text-amber-400 hover:bg-gray-100 dark:hover:bg-gray-800/80 transition-colors"
                                            title={theme === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode"}
                                        >
                                            {theme === "dark" ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-indigo-500 dark:text-indigo-300" />}
                                        </button>
                                        <button
                                            onClick={() => { navigate('/sop'); setIsMobileMenuOpen(false); }}
                                            className="p-2 rounded-xl text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800/80 transition-colors"
                                            title="Documentation"
                                        >
                                            <FileText className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={handleLogoutClick}
                                            className="p-2 rounded-xl text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                                            title="Logout"
                                        >
                                            <LogOut className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>

                                {/* Client Selector (if client level exists) */}
                                {level?.name && (
                                    <div className="mt-3 relative">
                                        <button
                                            onClick={() => setIsMobileClientDropdownOpen(prev => !prev)}
                                            className="w-full bg-orange-500/10 dark:bg-gradient-to-r dark:from-orange-500/15 dark:to-amber-600/15 border border-orange-300 dark:border-orange-500/30 hover:border-orange-400 dark:hover:border-orange-500/50 rounded-xl px-3 py-2 flex items-center justify-between gap-2 transition-all duration-200"
                                        >
                                            <div className="flex items-center gap-2 min-w-0">
                                                <div className="w-2 h-2 bg-orange-500 dark:bg-orange-400 rounded-full animate-pulse flex-shrink-0" />
                                                <span className="text-orange-700 dark:text-orange-300 text-xs font-medium truncate">
                                                    Client: <span className="font-semibold text-orange-900 dark:text-orange-200">{clientName}</span>
                                                </span>
                                            </div>
                                            <ChevronDown className={`w-3.5 h-3.5 text-orange-500 dark:text-orange-400/80 transition-transform duration-200 flex-shrink-0 ${isMobileClientDropdownOpen ? "rotate-180" : ""}`} />
                                        </button>

                                        {/* Client Dropdown in mobile view */}
                                        {isMobileClientDropdownOpen && (
                                            <div className="mt-2 bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-700/80 rounded-xl overflow-hidden shadow-xl max-h-48 flex flex-col">
                                                <div className="p-2 border-b border-gray-100 dark:border-gray-800">
                                                    <div className="relative">
                                                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 dark:text-gray-500" />
                                                        <input
                                                            type="text"
                                                            placeholder="Filter clients..."
                                                            value={clientSearchTerm}
                                                            onChange={e => setClientSearchTerm(e.target.value)}
                                                            className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700/70 rounded-lg pl-7 pr-2 py-1 text-[11px] text-gray-900 dark:text-gray-200 focus:outline-none focus:border-orange-500/50"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="overflow-y-auto custom-scroll py-1 max-h-36">
                                                    <button
                                                        onClick={() => {
                                                            handleClientSelect({ name: "global" });
                                                            setIsMobileClientDropdownOpen(false);
                                                        }}
                                                        className="w-full px-3 py-1.5 flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
                                                    >
                                                        <Globe className="w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400" />
                                                        <span className="text-[11px] font-bold text-gray-800 dark:text-gray-200 flex-1">Global</span>
                                                        {level.name === "global" && <CheckCircle2 className="w-3.5 h-3.5 text-orange-500" />}
                                                    </button>
                                                    {filteredClients.map(client => (
                                                        <button
                                                            key={client.id}
                                                            onClick={() => {
                                                                handleClientSelect({
                                                                    id: client.id,
                                                                    name: client.name,
                                                                    display_name: client.display_name,
                                                                    client_id: client.client_id,
                                                                    install_first_agent: client.install_first_agent,
                                                                });
                                                                setIsMobileClientDropdownOpen(false);
                                                            }}
                                                            className="w-full px-3 py-1.5 flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
                                                        >
                                                            <Building className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-[11px] font-bold text-gray-800 dark:text-gray-200 truncate">{client.name}</p>
                                                                <p className="text-[9px] text-gray-500 dark:text-gray-400 truncate">{client.display_name || "Enterprise Client"}</p>
                                                            </div>
                                                            {level.id === client.id && <CheckCircle2 className="w-3.5 h-3.5 text-orange-500" />}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* App Search Bar */}
                                <div className="mt-3 relative">
                                    <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                    <input
                                        type="text"
                                        value={mobileSearchTerm}
                                        onChange={e => setMobileSearchTerm(e.target.value)}
                                        placeholder="Search apps..."
                                        className="w-full bg-gray-100 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700/80 rounded-xl pl-8 pr-8 py-2 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-orange-500/70 transition-colors"
                                    />
                                    {mobileSearchTerm && (
                                        <button
                                            onClick={() => setMobileSearchTerm("")}
                                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 dark:hover:text-white p-0.5"
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Grid of Apps / Dock Items */}
                            <div className="p-3 overflow-y-auto custom-scroll max-h-[46vh] flex-1">
                                {mobileFilteredItems.length > 0 ? (
                                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
                                        {mobileFilteredItems.map(item => {
                                            const active = isActive(item.path);
                                            return (
                                                <button
                                                    key={item.id}
                                                    onClick={() => {
                                                        navigate(item.path);
                                                        setIsMobileMenuOpen(false);
                                                        setMobileSearchTerm("");
                                                    }}
                                                    className={`flex flex-col items-center justify-center p-2.5 rounded-2xl border transition-all duration-200 text-center group ${active
                                                        ? "bg-orange-500/15 border-orange-500/50 shadow-md shadow-orange-500/10"
                                                        : "bg-gray-50 dark:bg-gray-800/50 border-gray-200/80 dark:border-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-700"
                                                        }`}
                                                >
                                                    <div
                                                        className={`w-10 h-10 rounded-xl flex items-center justify-center mb-1.5 transition-transform group-hover:scale-110 shadow-sm border ${item.color || "bg-white dark:bg-gray-800"
                                                            } ${active
                                                                ? "border-orange-500 ring-2 ring-orange-400 ring-offset-1 ring-offset-white dark:ring-offset-gray-900"
                                                                : "border-gray-200 dark:border-transparent"
                                                            }`}
                                                    >
                                                        {typeof item.icon === "string" ? (
                                                            <img src={item.icon} className="w-5 h-5 object-contain" alt={item.label} />
                                                        ) : (
                                                            <item.icon className={`w-5 h-5 ${item.iconColor || "text-gray-800"} dark:text-white`} />
                                                        )}
                                                    </div>
                                                    <span className={`text-[11px] font-medium leading-tight line-clamp-2 ${active ? "text-orange-600 dark:text-orange-300 font-semibold" : "text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white"
                                                        }`}>
                                                        {item.label}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="py-8 text-center text-xs text-gray-500 dark:text-gray-400">
                                        No applications found.
                                    </div>
                                )}
                            </div>

                            {/* Footer: AI Assistant trigger */}
                            <div className="p-3 border-t border-gray-100 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-950/60 flex items-center justify-between gap-3">
                                <button
                                    onClick={() => {
                                        setIsMobileMenuOpen(false);
                                        setIsChatOpen(true);
                                    }}
                                    className="w-full flex items-center justify-center gap-2.5 py-2.5 px-3 rounded-xl bg-orange-500/10 dark:bg-gradient-to-r dark:from-orange-500/20 dark:via-amber-500/20 dark:to-orange-500/20 hover:bg-orange-500/15 dark:hover:from-orange-500/30 dark:hover:to-amber-500/30 border border-orange-300/60 dark:border-orange-500/30 text-orange-600 dark:text-orange-300 text-xs font-semibold transition-all duration-200 group"
                                >
                                    <img src="/owl.gif" alt="Owl Mascot" className="w-6 h-6 object-contain group-hover:scale-110 transition-transform" />
                                    <span>Chat with AI Assistant</span>
                                </button>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* ── Floating Profile Panel ────────────────────────────────────── */}
            {profileOpen && (
                <div
                    ref={profileRef}
                    style={{ position: "fixed", top: profilePosition.top, left: profilePosition.left }}
                    className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-xl p-3 w-48 shadow-2xl z-50 text-gray-900 dark:text-white transition-colors"
                >
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-3 p-2 border-b border-gray-100 dark:border-gray-700 pb-3">
                            <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                                <User className="w-4 h-4 text-white" />
                            </div>
                            <div>
                                <p className="text-gray-900 dark:text-white text-sm font-medium">{userName || "User"}</p>
                                <p className="text-gray-500 dark:text-gray-400 text-xs">{role || "Member"}</p>
                            </div>
                        </div>
                        {/* <button
                            onClick={toggleTheme}
                            className="flex items-center justify-between gap-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 transition-colors duration-200 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                        >
                            <div className="flex items-center gap-3">
                                {theme === "dark" ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-indigo-500 dark:text-indigo-300" />}
                                <span className="text-sm font-medium">{theme === "dark" ? "Light Mode" : "Dark Mode"}</span>
                            </div>
                            <span className="text-[10px] uppercase font-semibold tracking-wider text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
                                {theme}
                            </span>
                        </button> */}
                        <button
                            onClick={() => { navigate('/sop'); setProfileOpen(false); }}
                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 transition-colors duration-200 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                        >
                            <FileText className="w-4 h-4" />
                            <span className="text-sm font-medium">Documentation</span>
                        </button>
                        <button
                            onClick={handleLogoutClick}
                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/20 transition-colors duration-200 text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300"
                        >
                            <LogOut className="w-4 h-4" />
                            <span className="text-sm font-medium">Logout</span>
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

// ── DockIcon ──────────────────────────────────────────────────────────────────
interface DockItem {
    id: string;
    label: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>> | string;
    path: string;
    color?: string;
    iconColor?: string;
    clientOnly?: boolean;
    globalOnly?: boolean;
    permissionKey?: string;
    permissionkey?: string;
    hiddenFromDock?: boolean;
}

const DockIcon = ({
    item,
    active,
    onClick,
}: {
    item: DockItem;
    active: boolean;
    onClick: () => void;
}) => (
    <div className="relative group flex-shrink-0">
        <div className="absolute -top-11 left-1/2 -translate-x-1/2 
                px-2 py-1 rounded bg-gray-900/95 dark:bg-gray-800/95 border border-gray-700 
                text-white text-xs opacity-0 group-hover:opacity-100 
                transition-opacity duration-200 pointer-events-none 
                whitespace-nowrap z-50">

            {item.label}

            {/* Arrow */}
            <div className="absolute left-1/2 -bottom-1.5 -translate-x-1/2 
                  w-2.5 h-2.5 bg-gray-900 dark:bg-gray-800 border-r border-b border-gray-700 
                  rotate-45"></div>
        </div>
        <motion.button
            onClick={onClick}
            initial={{ scale: 1 }}
            whileHover={{ scale: 1.15, translateY: -4 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            className={`w-10 h-10 flex items-center justify-center rounded-xl border transition-all duration-200 flex-shrink-0 shadow-sm
                ${item.color || "bg-white dark:bg-gray-800/70"}
                ${active
                    ? "border-orange-500 ring-2 ring-orange-500/40 shadow-md shadow-orange-500/20"
                    : "border-gray-200 dark:border-transparent hover:border-gray-300 dark:hover:border-transparent hover:brightness-105 dark:hover:brightness-110"
                }`}
        >
            {typeof item.icon === "string" ? (
                <img src={item.icon} className="w-6 h-6 object-contain" alt={item.label} />
            ) : (
                <item.icon className={`w-4 h-4 ${item.iconColor || "text-gray-700"} dark:text-white`} />
            )}
        </motion.button>
    </div>
);